import Container from '@material-ui/core/Container';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(10, 0),
    backgroundColor: theme.palette.common.white,
    '& a': {
      font: 'inherit',
      textDecoration: 'none',
      color: theme.palette.primary.main,
      '&:hover': {
        textDecoration: 'underline',
        color: theme.palette.primary.dark
      }
    }
  },
  title: {
    marginBottom: theme.spacing(7),
    fontSize: 36,
    textTransform: 'uppercase',
    lineHeight: 1.27
  },
  contentWrap: {
    padding: theme.spacing(7, 0)
  },
  subTitle: {
    marginBottom: theme.spacing(7),
    fontSize: 19,
    fontWeight: 300,
    textTransform: 'uppercase',
    lineHeight: 1.1
  },
  text: {
    marginBottom: theme.spacing(7),
    fontSize: 16,
    fontWeight: 300,
    lineHeight: 1.75,
    '&:last-of-type': {
      marginBottom: 0
    }
  },
  list: {
    padding: 0,
    margin: theme.spacing(0, 0, 7, 5),
    fontSize: 16,
    fontWeight: 300,
    lineHeight: 1.75
  },
  listText: {
    paddingLeft: theme.spacing(7.5)
  }
}));

const TermsContent = () => {
  const { t } = useTranslation();
  const classes = useStyles({});

  return (
    <section className={classes.root}>
      <Container maxWidth="xl">
        <Typography className={classes.title} variant="h1">
          {t('termsAndConditionsPage.title')}
        </Typography>
        <Typography className={classes.text} variant="body1">
          This Terms of Use Agreement (this “Agreement”) is a legal agreement
          between you and ROAR (“ROAR”, “we”, “us” or “our”) regarding ROAR’s
          application (the “ROAR App”), which you have chosen to download from:
        </Typography>
        <ol className={classes.list}>
          <li>
            the Apple iPhone store website (the “Apple Site”) located at&nbsp;
            <a href="http://www.apple.com/iphone/appstore">
              http://www.apple.com/iphone/appstore
            </a>
            &nbsp;or;
          </li>
          <li>
            the Google Play store website (the “Google Site “) located at&nbsp;
            <a href="https://play.google.com/store?hl=en">
              https://play.google.com/store?hl=en
            </a>
            .
          </li>
        </ol>
        <Typography className={classes.text} variant="body1">
          Both you and we acknowledge that this Agreement is between us and you,
          and that Apple, Inc. and its subsidiaries (collectively, “Apple”) and
          Google, Inc. and its subsidiaries (collectively, “Google”) are not
          parties to this Agreement.
          <br />
          The procedures for modifying or changing this Agreement in the future
          are discussed in Section 7.
        </Typography>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            GLOBAL TERMS AND CONDITIONS
          </Typography>
          <Typography className={classes.text} variant="body1">
            This Agreement is conditional upon your agreement also to:
          </Typography>
          <ol className={classes.list}>
            <li>
              The Apple Site Terms of Service&nbsp;
              <a href="http://www.apple.com/legal/internet-services/itunes/us/terms.html">
                http://www.apple.com/legal/internet-services/itunes/us/terms.html
              </a>
              &nbsp;with Apple (the “Apple Terms of Service”); and
            </li>
            <li>
              The Google Site Terms of Service&nbsp;
              <a href="https://play.google.com/intl/en_us/about/play-terms.html">
                https://play.google.com/intl/en_us/about/play-terms.html
              </a>
              &nbsp;with Google (the “Google Terms of Service”).
            </li>
          </ol>
          <Typography className={classes.text} variant="body1">
            Upon valid acceptance of this Agreement, you agree and understand
            that you are bound by the Apple Terms of Service and Google Terms of
            Service.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            1. YOUR USE OF THE ROAR APP
          </Typography>
          <Typography className={classes.text} variant="body1">
            This ROAR App permits you to use the ROAR software application for
            mobile devices (“ROAR App”) and the ROAR information technology
            augmented reality and image recognition platform (“ROAR Services”)
            that ROAR may make available from time to time from your mobile
            device (collectively, “Device”).
            <br />
            Once you download the ROAR App onto your Device and accept this
            Agreement, you may use the ROAR App to use the ROAR Services.
            <br />
            If you wish to use the ROAR App and the ROAR Services, you must also
            have read and agreed to the ROAR Privacy Policy. A copy of the ROAR
            Privacy Policy can be found at&nbsp;
            <a href="https://www.theroar.io">www.theroar.io</a>&nbsp;and ROAR
            mobile application
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            2. LICENSE GRANT
          </Typography>
          <Typography className={classes.text} variant="body1">
            We grant you a limited, terminable, non – sub-licensable,
            non-transferable, non-exclusive license to install and use the ROAR
            App on any Device that you own or control solely:
          </Typography>
          <ol className={classes.list}>
            <li>
              For your personal use in connection with using the ROAR Services;
              and
            </li>
            <li>
              As permitted by the Apple Terms of Service or Google Terms of
              Service (as applicable).
            </li>
          </ol>
          <Typography className={classes.text} variant="body1">
            This Agreement will also govern any software upgrades provided by
            ROAR that replace and/or supplement the original ROAR App, unless
            such upgrades are accompanied by a separate license, in which case
            the terms of that license will govern.
            <br />
            Neither Apple nor Google has any obligation to provide you with any
            updates, maintenance or support services for the ROAR App.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            3. LICENSE RESTRICTIONS AND USAGE RULES
          </Typography>
          <Typography className={classes.text} variant="body1">
            3.1 LICENSE RESTRICTIONS
            <br />
            You may not use the ROAR App on any Device that you do not own or
            control, and you may not distribute or make the ROAR App available
            over a network where it could be used by multiple devices at the
            same time.
            <br />
            You may not rent, lease, lend, sell, redistribute or sublicense the
            ROAR App.
            <br />
            You may not copy (except as expressly permitted by this Agreement
            and the usage rules described in Section 3.2), decompile, reverse
            engineer, disassemble, attempt to derive the source code of, modify
            or create derivative works of the ROAR App, any updates or any part
            thereof (except to the extent any foregoing restriction is permitted
            by applicable law).
            <br />
            You represent and warrant that you will use the ROAR App and the
            ROAR Services in accordance with all applicable contracts, laws,
            rules and regulations.
          </Typography>
          <Typography className={classes.text} variant="body1">
            3.2 USAGE RULES
            <br />
            You acknowledge that the ROAR App contains security technology that
            limits your usage of the ROAR App to the following applicable usage
            rules.
            <br />
            Whether or not the ROAR App is limited by security technology, you
            agree to use the ROAR App in compliance with the applicable usage
            rules:
          </Typography>
          <ol className={classes.list}>
            <li>
              Your use of the ROAR App is conditioned upon your prior acceptance
              of the terms of this Agreement;
            </li>
            <li>
              You will not attempt to, or encourage or assist any other person
              to circumvent or modify any security technology or software that
              is part of the Service (as defined in the Apple Terms of Service)
              or used to administer the Usage Rules (as defined in the Apple
              Terms of Service);
            </li>
            <li>
              The delivery of the ROAR App does not transfer to you any
              promotional use rights in the ROAR App;
            </li>
            <li>
              You shall be able to manually sync the ROAR App from at least one
              authorized device to devices that have manual sync mode, provided
              that the ROAR App is associated with an Account (as defined in the
              Apple Terms of Service) on the primary iTunes authorized device,
              where the primary iTunes-authorized device is the one that was
              first synced with the device, or the one that you subsequently
              designate as primary using iTunes;
            </li>
            <li>
              You acknowledge and agree that you are solely and entirely
              responsible for the digital content you use, including but not
              limited to text, images, graphics, videos, 3D models, web links
              etc.;
            </li>
            <li>
              You agree not to post, upload to, transmit, distribute, store,
              create or otherwise publish through the ROAR App any of the
              following:
              <p className={classes.listText}>
                a&#41;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; User Generated Content that
                is false, misleading, unlawful, libelous, defamatory, obscene,
                pornographic, indecent, lewd, suggestive, harassing,
                threatening, invasive of privacy or publicity rights, abusive,
                inflammatory, fraudulent or otherwise objectionable;
              </p>
              <p className={classes.listText}>
                b&#41;&nbsp;&nbsp;&nbsp;&nbsp; User Generated Content that may
                infringe any patent, trademark, trade secret, copyright or other
                intellectual or proprietary right of any party. By posting any
                User Generated Content, you represent and warrant that you have
                the lawful right to distribute and reproduce such User Content;
              </p>
              <p className={classes.listText}>
                c&#41;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; User Generated Content that
                impersonates any person or entity or otherwise misrepresents
                your affiliation with a person or entity;
              </p>
              <p className={classes.listText}>
                d&#41;&nbsp;&nbsp;&nbsp;&nbsp; Private or confidential
                information of any third party, including, without limitation,
                addresses, phone numbers, e-mail addresses, Social Security
                numbers and credit card numbers; viruses, spyware, malware,
                corrupted data or other harmful, disruptive or destructive
                files;
              </p>
              <p className={classes.listText}>
                e&#41;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; User Generated Content
                that, in the sole judgment of ROAR, is objectionable or which
                restricts or inhibits any other person from using or enjoying
                the Roar App, or which may expose ROAR or&nbsp;
                <span>its users to any harm or liability of any type.</span>
              </p>
            </li>
            <li>
              Should your content places a significant burden on our platform or
              negatively impacts the work of our website, we reserve the right
              to restrict or temporarily disable access to your digital content;
              and
            </li>
            <li>
              To improve the efficiency and performance of our platform, we
              reserve the right to remove any user generated content that has
              been inactive. For the purposes of this article, inactive means
              the user generated content that had no activity during two
              calendar months.
            </li>
          </ol>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            4. Subscription plans
          </Typography>
          <ol className={classes.list}>
            <li>
              Part of our services is available only on a subscription basis.
              Subscription plans are published on the website. By applying for a
              subscription plan on the ROAR website, you agree to the terms set
              forth in this section.
            </li>
            <li>
              The subscription will be activated as soon as ROAR has been able
              to verify your payment.
            </li>
            <li>
              The subscription is charged automatically once per month and
              entered on the basis of auto-renewal. Unless you change your
              subscription plan to free, we will renew your subscription
              automatically by withdrawing the subscription price from your
              authorized credit card account on the next billing cycle.
            </li>
            <li>
              Failure to charge your card account will result in an immediate
              suspension&nbsp;of your subscription.
            </li>
            <li>
              We have various subscription plans with their respective prices,
              which can be reviewed from time to time.
            </li>
            <li>
              We reserve the right to change the subscription plans and prices
              at any time without further notice. Such changes will come into
              effect for your subscription on the next renewal date.
            </li>
            <li>
              <a
                href="https://theroar.io/press/ROAR_Refund_Policy.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Charges, Refund and Cancellation Policy
              </a>
            </li>
          </ol>
        </div>
        <p>&nbsp;</p>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            5. TERMINATION AND SURVIVAL
          </Typography>
          <ol className={classes.list}>
            <li>
              If you breach this Agreement, including but not limited to
              violating the Usage Rules in Section 3.2 (6) above, we may
              immediately terminate this Agreement, but all provisions of this
              Agreement except Section 2 (“License Grant”) will survive
              termination and continue in effect.
            </li>
            <li>
              We may terminate this Agreement without cause, for any reason by
              giving you at least one calendar months’ notice[SK1][RF2].
            </li>
            <li>
              Upon termination, either for cause as provided in Section 5 (1) or
              without cause as provided in Section 5 (2), you must destroy all
              copies of the ROAR App.
            </li>
          </ol>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            6. LICENSE TO ROAR OF USER GENERATED CONTENT
          </Typography>
          <Typography className={classes.text} variant="body1">
            By using the ROAR App to view digital content from our servers, you
            are also submitting digital content viewed by you on your Device to
            our servers.
            <br />
            You agree to grant, and thereby grant, to us a non-exclusive,
            perpetual, transferable, sub-licensable, royalty-free,
            non-revocable, worldwide license to copy, modify, and otherwise use
            content viewed by you using your Device (“User Generated Content”)
            to make the ROAR Service available to you and for our general
            business purposes.
            <br />
            You hereby acknowledge and agree that the use of the ROAR App and
            the ROAR Service is sufficient consideration for the grant of this
            license to us.
            <br />
            You represent, warrant and undertake that the User Generated Content
            does not and will not infringe the rights (including the
            Intellectual Property Rights) of any third party and shall comply
            with all applicable laws.
            <br />
            You shall indemnify ROAR and shall keep ROAR indemnified from and
            against all losses arising out of or in connection with any claim or
            allegation that the User Generated Content infringes all applicable
            laws and/or the rights (including, without limitation, the
            Intellectual Property Rights) of any third party.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            7. PROPRIETARY RIGHTS
          </Typography>
          <Typography className={classes.text} variant="body1">
            We reserve all intellectual property rights in the ROAR App and the
            ROAR Services, except for the rights expressly granted in this
            Agreement.
            <br />
            You may not remove or alter any trademark, logo, copyright or other
            proprietary notice in or on the ROAR App.
            <br />
            This Agreement does not grant you any right to use the trademarks,
            service marks or logos of ROAR or its licensors.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            8. MODIFICATION OF THIS AGREEMENT
          </Typography>
          <Typography className={classes.text} variant="body1">
            We may need to modify this Agreement from time to time. In most
            circumstances the reasons for these modifications are to reflect
            changes in the law, to meet regulatory requirements or to reflect
            new industry guidance and codes of practice.
            <br />
            If we modify the Agreement, we will post a notice on the ROAR
            website located at https://www.theroar.io. If you do not wish to
            accept the new agreement, you may terminate this Agreement by
            deleting the ROAR App.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            9. INDEMNIFICATION
          </Typography>
          <Typography className={classes.text} variant="body1">
            9.1 INDEMNITY
            <br />
            Provided you are in compliance with these Terms of Use, we will at
            our expense and option defend or settle any claim brought against
            you alleging that the ROAR App or your possession and use of the
            ROAR App breaches any third-party intellectual property rights. We,
            and not Apple, will be solely responsible for the investigation,
            defense, settlement and discharge of this third-party intellectual
            property infringement claim.
          </Typography>
          <Typography className={classes.text} variant="body1">
            9.2 LIMITATION
            <br />
            We will have no obligation with respect to any claim under Section
            9.1 unless:
            <br />
            You promptly notify us about the claim;
            <br />
            You allow us to have sole control of the defense and settlement of
            such claim; and
            <br />
            You provide us with reasonable assistance in connection with our
            defense and settlement of such claim.
          </Typography>
          <Typography className={classes.text} variant="body1">
            9.3 OUR OPTIONS
            <br />
            If the ROAR App infringes or misappropriates, or in our reasonable
            determination is likely to infringe or misappropriate any third
            party’s intellectual property rights, then we may, at our sole
            option and expense:
            <br />
            Obtain from the third party the right to continue to use the ROAR
            App consistent with the rights granted hereunder; or
            <br />
            Modify the ROAR App to avoid and eliminate such infringement or
            misappropriation.
          </Typography>
          <Typography className={classes.text} variant="body1">
            9.4 EXCLUSIONS
            <br />
            We will not be liable for any infringement based on:
            <br />
            Modification of the ROAR App by anyone other than us; or
            <br />
            The combination of the ROAR App with other software, items or
            processes not furnished by us if such infringement would have been
            avoided by the use of the Software alone.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            10. DISCLAIMER OF WARRANTY
          </Typography>
          <Typography className={classes.text} variant="body1">
            The ROAR App has been developed with, and the ROAR Services, are
            provided with, reasonable care and skill. ROAR does not make any
            other promises or warranties about the ROAR App and in particular
            does not warrant that:
          </Typography>
          <ol className={classes.list}>
            <li>
              The ROAR App will provide uninterrupted or error-free use and the
              ROAR Services will be uninterrupted or error-free; and
            </li>
            <li>
              The ROAR App and ROAR Services will operate free from loss,
              corruption, attack, viruses, interference, hacking, or other
              security intrusion. You shall be responsible for backing up your
              own system.
            </li>
          </ol>
          <Typography className={classes.text} variant="body1">
            In no case shall ROAR be liable for any loss or damage caused by the
            ROAR App where:
          </Typography>
          <ol className={classes.list}>
            <li>
              There is no breach of a legal duty of care owed to you by ROAR;
            </li>
            <li>
              It is not a reasonably foreseeable result of any such breach;
            </li>
            <li>
              Any increase in loss or damage results from breach by you of any
              term of this Agreement;
            </li>
            <li>
              It results from a decision by ROAR, to suspend or terminate your
              access to the ROAR App or the ROAR Service, or to take any other
              action during the investigation of a suspected breach or as a
              result of a breach, of this Agreement;
            </li>
            <li>
              It relates to loss of income, business or profits, or any loss of
              data or corruption of data in connection with your use of the ROAR
              App or the ROAR Service; or
            </li>
            <li>The loss or damage is indirect.</li>
          </ol>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            11. LIMITATION OF LIABILITY
          </Typography>
          <Typography className={classes.text} variant="body1">
            IN NO EVENT SHALL WE OR ANY OF OUR CORPORATE AFFILIATES, INDEPENDENT
            CONTRACTORS, SERVICE PROVIDERS OR CONSULTANTS, OR ANY OF THEIR
            RESPECTIVE DIRECTORS, EMPLOYEES AND AGENTS, BE LIABLE FOR ANY
            DIRECT, SPECIAL, INDIRECT OR CONSEQUENTIAL DAMAGES, OR ANY OTHER
            DAMAGES OF ANY KIND, INCLUDING BUT NOT LIMITED TO, LOSS OF USE, LOSS
            OF PROFITS OR LOSS OF DATA, WHETHER IN AN ACTION IN CONTRACT, TORT
            (INCLUDING BUT NOT LIMITED TO NEGLIGENCE) OR OTHERWISE, ARISING OUT
            OF OR IN ANY WAY RELATED TO OR CONNECTED WITH ANY USE OF THIS
            WEBSITE, OR THE CONTENT CONTAINED IN OR ACCESSED THROUGH THIS
            WEBSITE
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            12. SUSPENSION OF THE ROAR APP AND ROAR SERVICES
          </Typography>
          <Typography className={classes.text} variant="body1">
            We can, at any time, suspend your use of the ROAR App and/or ROAR
            Services. We will do this if we think it is reasonably necessary
            because:
          </Typography>
          <ol className={classes.list}>
            <li>
              We have concerns about the security of the ROAR App or ROAR
              Services;
            </li>
            <li>
              We are obliged to do so in order to comply with the order of a
              court, regulatory authority or other government body; or
            </li>
            <li>
              We reasonably believe that you are using the ROAR App or ROAR
              Services in a manner that is inconsistent with or breaches any of
              our, Apple or Google’s usage rules.
            </li>
            <li>
              We believe that the content created/posted/saved by you on ROAR
              App or ROAR website violates the Usage Rules.
            </li>
          </ol>
          <Typography className={classes.text} variant="body1">
            We will usually give you advance notice of any suspension and tell
            you why. However, we will not do so if this would compromise our
            reasonable security measures or it is unlawful to do this.
            Occasionally, we may not be able to contact you to give you advance
            notice.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            13. INJUNCTIVE RELIEF
          </Typography>
          <Typography className={classes.text} variant="body1">
            We will have the right to seek injunctive relief to enforce this
            Agreement or to stop or prevent an infringement of proprietary or
            other third-party rights.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            14. CHOICE OF LAW AND JURISDICTION
          </Typography>
          <Typography className={classes.text} variant="body1">
            In the unlikely event that you wish to bring legal action against us
            these terms shall be governed by and construed in accordance with
            the laws of the State of New Jersey, USA applicable to agreements
            made and to be entirely performed within the State of New Jersey
            (even if your use is outside of the State of New Jersey), without
            resort to its conflict of law provisions. You agree that any action
            at law or in equity arising out of or relating to this website or
            these terms shall be filed only in the state or federal courts
            located in the State of New Jersey and you hereby irrevocably and
            unconditionally consent and submit to the exclusive jurisdiction of
            such courts.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            15. ENTIRE AGREEMENT
          </Typography>
          <Typography className={classes.text} variant="body1">
            This Agreement constitutes the entire agreement between you and us
            regarding the use of this ROAR App and supersedes any prior or
            contemporaneous understandings and agreements between you and us
            related to its subject matter.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            16. SECTION TITLES
          </Typography>
          <Typography className={classes.text} variant="body1">
            The section titles in this Agreement are for convenience only and
            have no legal or contractual effect.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            17. NON-WAIVER
          </Typography>
          <Typography className={classes.text} variant="body1">
            Our failure to exercise or enforce any right or provision of this
            Agreement shall not operate as a waiver of such right or provision.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            18. SEVERABILITY
          </Typography>
          <Typography className={classes.text} variant="body1">
            The parties agree that each provision of this Agreement is intended
            to be construed to be enforceable to the fullest extent possible. If
            any provision or part of a provision of this Agreement is held to be
            unlawful, void, or unenforceable, that provision or part of the
            provision shall be deemed severable from this Agreement and not
            affect the validity and enforceability of any remaining provisions.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            19. ASSIGNMENT
          </Typography>
          <Typography className={classes.text} variant="body1">
            You may not assign your rights under this Agreement to any third
            party; we may assign our rights under this Agreement by providing
            you with notice of such assignment. If you do not wish our rights
            under this Agreement to be assigned to a third party, you may
            terminate this Agreement by providing us with written notice of the
            same.
          </Typography>
        </div>
        <div className={classes.contentWrap}>
          <Typography className={classes.subTitle} variant="h4">
            20. PRODUCT QUESTIONS, COMMENTS, CLAIMS AND CONTACT INFORMATION
          </Typography>
          <Typography className={classes.text} variant="body1">
            We, and not Apple or Google, are responsible for addressing any
            questions, comments or claims relating to the ROAR App and/or your
            use of the ROAR App, including but not limited to:
          </Typography>
          <ol className={classes.list}>
            <li>product liability claims;</li>
            <li>personal data claims;</li>
            <li>
              any claim that the ROAR App fails to conform to any applicable
              legal or regulatory requirement; and
            </li>
            <li>
              Claims arising under consumer protection or similar legislation.
            </li>
          </ol>
          <Typography className={classes.text} variant="body1">
            If you have any comments or questions, please contact ROAR at&nbsp;
            <a href="mailto:support@theroar.io">support@theroar.io</a>
            <br />
            If you believe that any material within the ROAR App infringes upon
            your copyright, pursuant to the Digital Millennium Copyright Act,
            please send your written notice of infringement to:
            <br />
            ROAR
            <br />
            330 Washington St., # 172
            <br />
            Hoboken, NJ 07030
            <br />
            You may also email any claims to:&nbsp;
            <a href="mailto:support@theroar.io">support@theroar.io</a>
          </Typography>
        </div>
      </Container>
    </section>
  );
};

export default TermsContent;
